import React, { useState, useEffect, useCallback } from 'react';
import Pagination from './Pagination';
import './EsporResults.css';

const API = '/api';
const PAGE_SIZE = 100;

function EsporResults({ initialView, onViewConsumed }) {
  const [matches, setMatches] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [filterOptions, setFilterOptions] = useState({ players: [], leagues: [] });
  const [filterPlayer, setFilterPlayer] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [filterStartDate, setFilterStartDate] = useState('');
  const [filterEndDate, setFilterEndDate] = useState('');

  const [globalMode, setGlobalMode] = useState('gizle');
  const [rowModes, setRowModes] = useState({});

  const [sub, setSub] = useState(null);
  const [subMatches, setSubMatches] = useState([]);
  const [subPag, setSubPag] = useState({ page: 1, limit: PAGE_SIZE, total: 0, totalPages: 0 });
  const [subLoading, setSubLoading] = useState(false);
  const [subStats, setSubStats] = useState(null);
  const [subGlobalMode, setSubGlobalMode] = useState('gizle');
  const [subRowModes, setSubRowModes] = useState({});
  const [subResultFilter, setSubResultFilter] = useState(null);
  const [reverseTeam, setReverseTeam] = useState(true);
  const [only2X5, setOnly2X5] = useState(true);

  const token = localStorage.getItem('token');

  useEffect(() => {
    const now = new Date();
    const y = now.getFullYear(), m = String(now.getMonth()+1).padStart(2,'0'), d = String(now.getDate()).padStart(2,'0');
    setFilterStartDate(`${y}-${m}-01`);
    setFilterEndDate(`${y}-${m}-${d}`);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/finished-matches/filter-options`, { headers: { Authorization: `Bearer ${token}` } });
        const d = await r.json();
        if (d.success) setFilterOptions(d.data);
      } catch (e) { console.error(e); }
    })();
  }, [token]);

  const fetchMatches = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('page', page); p.set('limit', PAGE_SIZE);
      if (filterPlayer.trim()) p.set('player', filterPlayer.trim());
      if (filterStartDate) p.set('startDate', filterStartDate);
      if (filterEndDate) p.set('endDate', filterEndDate);
      if (only2X5) p.set('league', '2X5');
      const r = await fetch(`${API}/finished-matches?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) { setMatches(d.data); setPagination(d.pagination); }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [token, filterPlayer, filterStartDate, filterEndDate, only2X5]);

  useEffect(() => { if (filterStartDate && filterEndDate) fetchMatches(1); }, [fetchMatches, filterStartDate, filterEndDate]);

  const onPage = (p) => { fetchMatches(p); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  /* ── H2H ── */
  const openH2H = (match) => {
    const hp = match.homePlayer || playerOf(match.homeTeam);
    const ap = match.awayPlayer || playerOf(match.awayTeam);
    if (!hp || !ap) return;
    setSub({ type: 'h2h', homePlayer: hp, awayPlayer: ap, title: `${hp} vs ${ap}` });
    setSubStats(null); setSubGlobalMode('gizle'); setSubRowModes({}); setSubResultFilter(null); setReverseTeam(true);
    fetchH2H(hp, ap, 1);
  };
  const fetchH2H = async (hp, ap, page = 1) => {
    setSubLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('homePlayer', hp); p.set('awayPlayer', ap);
      p.set('page', page); p.set('limit', PAGE_SIZE);
      const r = await fetch(`${API}/finished-matches/head-to-head?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) { setSubMatches(d.data); setSubPag(d.pagination); setSubStats(d.stats || null); }
    } catch (e) { console.error(e); }
    setSubLoading(false);
  };

  /* ── Player ── */
  const openPlayer = (name) => {
    if (!name) return;
    setSub({ type: 'player', name, title: `${name} — Tüm Maçlar` });
    setSubStats(null); setSubGlobalMode('gizle'); setSubRowModes({}); setSubResultFilter(null); setReverseTeam(true);
    fetchPlayer(name, 1);
  };
  const fetchPlayer = async (name, page = 1) => {
    setSubLoading(true);
    try {
      const p = new URLSearchParams();
      p.set('name', name); p.set('page', page); p.set('limit', PAGE_SIZE);
      const r = await fetch(`${API}/finished-matches/player?${p}`, { headers: { Authorization: `Bearer ${token}` } });
      const d = await r.json();
      if (d.success) { setSubMatches(d.data); setSubPag(d.pagination); setSubStats(d.stats || null); }
    } catch (e) { console.error(e); }
    setSubLoading(false);
  };

  useEffect(() => {
    if (!initialView) return;
    if (initialView.type === 'player' && initialView.name) openPlayer(initialView.name);
    else if (initialView.type === 'h2h' && initialView.homePlayer && initialView.awayPlayer) openH2H({ homePlayer: initialView.homePlayer, awayPlayer: initialView.awayPlayer });
    if (onViewConsumed) onViewConsumed();
  }, [initialView]); // eslint-disable-line

  const closeSub = () => { setSub(null); setSubMatches([]); setSubStats(null); };
  const onSubPage = (p) => {
    if (sub.type === 'h2h') fetchH2H(sub.homePlayer, sub.awayPlayer, p);
    else fetchPlayer(sub.name, p);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  /* ── Helpers ── */
  const clubOf = (full) => { const m = full?.match(/^(.+?)\s*\(([^)]+)\)$/); return m ? m[1].trim() : full || '?'; };
  const playerOf = (full) => { const m = full?.match(/^(.+?)\s*\(([^)]+)\)$/); return m ? m[2].trim() : ''; };
  const DAYS_TR = ['Pazar', 'Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi'];
  const fmtTime = (d) => {
    if (!d) return { line1: '-', line2: '' };
    const dt = new Date(d);
    const now = new Date();
    const trOpts = { timeZone: 'Europe/Istanbul' };
    const dtDay = dt.toLocaleDateString('tr-TR', { ...trOpts, day: '2-digit', month: '2-digit', year: '2-digit' });
    const nowDay = now.toLocaleDateString('tr-TR', { ...trOpts, day: '2-digit', month: '2-digit', year: '2-digit' });
    const isToday = dtDay === nowDay;
    const time = dt.toLocaleTimeString('tr-TR', { ...trOpts, hour: '2-digit', minute: '2-digit' });
    const datePart = dtDay;
    const dayName = dt.toLocaleDateString('tr-TR', { ...trOpts, weekday: 'long' });
    const dayNameShort = DAYS_TR.find(d => dayName.toLowerCase().startsWith(d.toLowerCase())) || dayName;
    if (isToday) return { line1: time, line2: datePart };
    return { line1: `${dayNameShort} ${time}`, line2: datePart };
  };
  const modeOf = (id, gm, rm) => rm[id] || gm;
  const setMode = (id, mode, setter) => setter(prev => ({ ...prev, [id]: mode }));
  const setAllMode = (mode, gmSet, rmSet) => { gmSet(mode); rmSet({}); };

  /* ── Goal Timeline (Çizgi) ── */
  const renderGoalTimeline = (match) => {
    const hg = (match.goalEvents || []).filter(g => g.team === 'home').map(g => g.minute);
    const ag = (match.goalEvents || []).filter(g => g.team === 'away').map(g => g.minute);
    if (!hg.length && !ag.length) return <div className="er-no-goals">Gol yok</div>;
    return (
      <div className="er-timeline">
        <div className="er-tl-goals er-tl-home">
          {hg.map((min, i) => <span key={i} className="er-tl-pill er-tl-h" style={{ left: `${(min / 120) * 100}%` }}>{min}'</span>)}
        </div>
        <div className="er-tl-bar">
          <div className="er-tl-half" />
          {[15, 30, 45, 60, 75, 90, 105, 120].map(t => (
            <div key={t} className="er-tl-tick" style={{ left: `${(t / 120) * 100}%` }}><span className="er-tl-tick-l">{t}</span></div>
          ))}
        </div>
        <div className="er-tl-goals er-tl-away">
          {ag.map((min, i) => <span key={i} className="er-tl-pill er-tl-a" style={{ left: `${(min / 120) * 100}%` }}>{min}'</span>)}
        </div>
      </div>
    );
  };

  /* ── Goal List (Liste) ── */
  const renderGoalList = (match) => {
    const goals = (match.goalEvents || []).slice().sort((a, b) => a.minute - b.minute);
    if (!goals.length) return <div className="er-no-goals">Gol yok</div>;
    return (
      <div className="er-gl">
        {goals.map((g, i) => (
          <div key={i} className="er-gl-row">
            <span className="er-gl-left">{g.team === 'home' ? `${g.minute}'` : ''}</span>
            <span className="er-gl-ball">⚽</span>
            <span className="er-gl-right">{g.team === 'away' ? `${g.minute}'` : ''}</span>
          </div>
        ))}
      </div>
    );
  };

  /* ── Mode Buttons ── */
  const renderModeButtons = (id, current, setter) => (
    <div className="er-mode-btns">
      {[['cizgi', '📊'], ['liste', '📋'], ['gizle', '✕']].map(([m, icon]) => (
        <button key={m} className={`er-mbtn ${current === m ? 'er-mbtn-on' : ''}`}
          onClick={e => { e.stopPropagation(); setMode(id, m, setter); }}
          title={m.charAt(0).toUpperCase() + m.slice(1)}>{icon}</button>
      ))}
    </div>
  );

  const renderGlobalModeButtons = (gm, gmSet, rmSet) => (
    <div className="er-gmode">
      {[['cizgi', '📊 Çizgi'], ['liste', '📋 Liste'], ['gizle', '✕ Gizle']].map(([m, label]) => (
        <button key={m} className={`er-gmbtn ${gm === m ? 'er-gmbtn-on' : ''}`}
          onClick={() => setAllMode(m, gmSet, rmSet)}>{label}</button>
      ))}
    </div>
  );

  /* ── Match Cards ── */
  const renderCards = (data, gm, rm, rmSet, normalizePlayer = null) => {
    return (
      <div className="er-cards">
        {data.map((match) => {
          const id = match._id || match.code;
          let hp = match.homePlayer || playerOf(match.homeTeam);
          let ap = match.awayPlayer || playerOf(match.awayTeam);
          let hc = clubOf(match.homeTeam);
          let ac = clubOf(match.awayTeam);
          let hScore = match.homeScore;
          let aScore = match.awayScore;
          let htH = match.halfTimeHome;
          let htA = match.halfTimeAway;
          let goalEvents = match.goalEvents || [];

          // Reverse: normalizePlayer her zaman home tarafında görünsün
          const needSwap = normalizePlayer && hp.toLowerCase() !== normalizePlayer.toLowerCase();
          if (needSwap) {
            [hp, ap] = [ap, hp];
            [hc, ac] = [ac, hc];
            [hScore, aScore] = [aScore, hScore];
            [htH, htA] = [htA, htH];
            goalEvents = goalEvents.map(g => ({ ...g, team: g.team === 'home' ? 'away' : 'home' }));
          }

          const mode = modeOf(id, gm, rm);
          const hw = hScore > aScore;
          const aw = aScore > hScore;
          const t = fmtTime(match.startTime);
          const iy = htH != null ? `${htH}-${htA}` : null;
          return (
            <div key={id} className={`er-card ${needSwap ? 'er-card-reversed' : ''}`}>
              <div className="er-card-league">{match.leagueName || '-'}</div>
              <div className="er-card-body">
                <div className="er-card-time">
                  <div className="er-time1">{t.line1}</div>
                  <div className="er-time2">{t.line2}</div>
                </div>
                <div className={`er-card-player er-card-hp ${hw ? 'er-w' : aw ? 'er-l' : 'er-d'}`} onClick={() => openPlayer(hp)}>
                  <span className="er-pn">{hp || hc}</span>
                  <span className="er-cn">{hc}</span>
                </div>
                <div className="er-card-score" onClick={() => openH2H(match)}>
                  <span className={`er-sb ${hw ? 'er-sb-hw' : aw ? 'er-sb-aw' : 'er-sb-d'}`}>{hScore}-{aScore}</span>
                  {iy && <span className="er-iy">İY {iy}</span>}
                </div>
                <div className={`er-card-player er-card-ap ${aw ? 'er-w' : hw ? 'er-l' : 'er-d'}`} onClick={() => openPlayer(ap)}>
                  <span className="er-pn">{ap || ac}</span>
                  <span className="er-cn">{ac}</span>
                </div>
                <div className="er-card-actions">{renderModeButtons(id, mode, rmSet)}</div>
              </div>
              {mode !== 'gizle' && (
                <div className="er-card-detail">
                  {mode === 'cizgi' ? renderGoalTimeline({ ...match, goalEvents }) : renderGoalList({ ...match, goalEvents })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  /* ── Player Stats ── */
  const renderPlayerStats = (s, playerName) => {
    const toggle = (f) => setSubResultFilter(prev => prev === f ? null : f);
    return (
      <div className="er-ps">
        {[['Maç', s.total, '', null], ['Galibiyet', s.wins, 'er-ps-w', 'win'], ['Beraberlik', s.draws, 'er-ps-dr', 'draw'],
          ['Mağlubiyet', s.losses, 'er-ps-l', 'loss'], ['A. Gol', s.goalsFor, '', null], ['Y. Gol', s.goalsAgainst, '', null]
        ].map(([label, val, cls, fKey]) => (
          <div key={label}
            className={`er-ps-item ${cls} ${fKey ? 'er-ps-click' : ''} ${subResultFilter === fKey ? 'er-ps-active' : ''}`}
            onClick={fKey ? () => toggle(fKey) : undefined}>
            <span className="er-ps-num">{val}</span>
            <span className="er-ps-lbl">{label}</span>
          </div>
        ))}
      </div>
    );
  };

  /* ── H2H Stats ── */
  const renderH2HStats = (s) => {
    if (!s?.player1 || !s?.player2) return null;
    const { player1: p1, player2: p2 } = s;
    const rows = [
      ['Galibiyet', p1.wins, p2.wins],
      ['Beraberlik', p1.draws, p2.draws],
      ['Mağlubiyet', p1.losses, p2.losses],
      ['Atılan Gol', p1.goalsFor, p2.goalsFor],
      ['Yenilen Gol', p1.goalsAgainst, p2.goalsAgainst],
    ];
    return (
      <div className="er-h2h">
        <div className="er-h2h-hdr">
          <span className="er-h2h-n1">{p1.name}</span>
          <span className="er-h2h-vs">VS</span>
          <span className="er-h2h-n2">{p2.name}</span>
        </div>
        <div className="er-h2h-g">
          {rows.map(([label, v1, v2]) => (
            <React.Fragment key={label}>
              <div className="er-h2h-v er-h2h-v1">{v1}</div>
              <div className="er-h2h-lbl">{label}</div>
              <div className="er-h2h-v er-h2h-v2">{v2}</div>
            </React.Fragment>
          ))}
        </div>
        <div className="er-h2h-tot">{s.total} maç</div>
      </div>
    );
  };

  /* ══════════════════════ */
  /* Sub-view (Player/H2H) */
  /* ══════════════════════ */
  if (sub) {
    return (
      <div className="espor-results">
        <div className="er-hdr">
          <div className="er-hdr-l">
            <button className="er-back" onClick={closeSub}>← Geri</button>
            <h2>{sub.title}</h2>
            <span className="er-cnt">{subPag.total} maç</span>
            {sub.type === 'h2h' && (
              <button className="er-swap-btn" onClick={() => {
                setSub(prev => ({
                  ...prev,
                  homePlayer: prev.awayPlayer,
                  awayPlayer: prev.homePlayer,
                  title: `${prev.awayPlayer} vs ${prev.homePlayer}`,
                }));
              }} title="Oyuncuları değiştir">⇄</button>
            )}
          </div>
          <div className="er-hdr-r">
            {renderGlobalModeButtons(subGlobalMode, setSubGlobalMode, setSubRowModes)}
          </div>
        </div>

        {sub.type === 'player' && subStats && renderPlayerStats(subStats, sub.name)}
        {sub.type === 'h2h' && subStats && renderPlayerStats(subStats.player1, subStats.player1?.name)}

        <button
          className={`er-reverse-btn ${reverseTeam ? 'er-reverse-on' : ''}`}
          onClick={() => setReverseTeam(p => !p)}
        >
          🔄 Reverse Team
        </button>

        <Pagination page={subPag.page} totalPages={subPag.totalPages || 1} total={subPag.total} onPageChange={onSubPage} />

        {subLoading ? (
          <div className="er-loading"><div className="spinner" /><p>Yükleniyor...</p></div>
        ) : subMatches.length === 0 ? (
          <div className="er-empty"><span className="er-empty-icon">📭</span><p>Sonuç bulunamadı.</p></div>
        ) : (() => {
          const pName = sub.type === 'player' ? sub.name : (sub.homePlayer || '');
          const filtered = subResultFilter ? subMatches.filter(m => {
            const isHome = (m.homePlayer || '').toLowerCase() === pName.toLowerCase();
            const my = isHome ? m.homeScore : m.awayScore;
            const opp = isHome ? m.awayScore : m.homeScore;
            if (subResultFilter === 'win') return my > opp;
            if (subResultFilter === 'loss') return my < opp;
            return my === opp;
          }) : subMatches;
          return filtered.length === 0
            ? <div className="er-empty"><span className="er-empty-icon">📭</span><p>Filtre sonucu bulunamadı.</p></div>
            : renderCards(filtered, subGlobalMode, subRowModes, setSubRowModes, reverseTeam ? (sub.type === 'h2h' ? sub.homePlayer : sub.name) : null);
        })()}

        <Pagination page={subPag.page} totalPages={subPag.totalPages || 1} total={subPag.total} onPageChange={onSubPage} />
      </div>
    );
  }

  /* ══════════════════════ */
  /* Main View             */
  /* ══════════════════════ */
  return (
    <div className="espor-results">
      <div className="er-hdr">
        <div className="er-hdr-l">
          <h2>📊 Espor Sonuçları</h2>
          <span className="er-cnt">{pagination.total} maç</span>
        </div>
        <div className="er-hdr-r">
          {renderGlobalModeButtons(globalMode, setGlobalMode, setRowModes)}
          <button className={`er-gmbtn ${only2X5 ? 'er-gmbtn-on' : ''}`} onClick={() => setOnly2X5(p => !p)}>
            🏆 {only2X5 ? 'Sadece 2X5' : 'Tümü'}
          </button>
          <button className="er-refresh" onClick={() => fetchMatches(pagination.page)} disabled={loading}>
            {loading ? '⏳' : '🔄'} Yenile
          </button>
        </div>
      </div>

      <div className="er-filters">
        <div className="er-frow">
          <div className="er-fg"><label>Başlangıç</label><input type="date" value={filterStartDate} onChange={e => setFilterStartDate(e.target.value)} /></div>
          <div className="er-fg"><label>Bitiş</label><input type="date" value={filterEndDate} onChange={e => setFilterEndDate(e.target.value)} /></div>
          <div className="er-fg er-fg-ac">
            <label>Oyuncu</label>
            <input type="text" placeholder="Oyuncu ara..." value={filterPlayer}
              onChange={e => { setFilterPlayer(e.target.value); setShowSuggestions(true); }}
              onFocus={() => setShowSuggestions(true)} />
            {showSuggestions && filterPlayer.trim() && (() => {
              const q = filterPlayer.trim().toLowerCase();
              const hits = filterOptions.players.filter(p => p.toLowerCase().includes(q)).slice(0, 10);
              if (!hits.length) return null;
              return (
                <div className="er-ac-list">
                  <button className="er-ac-close" onClick={() => setShowSuggestions(false)}>✕</button>
                  {hits.map(p => (
                    <div key={p} className="er-ac-item" onClick={() => { setFilterPlayer(p); setShowSuggestions(false); }}>{p}</div>
                  ))}
                </div>
              );
            })()}
          </div>
          <div className="er-fg er-fa">
            <button className="er-fbtn" onClick={() => fetchMatches(1)}>🔍 Ara</button>
            <button className="er-fclear" onClick={() => { setFilterPlayer(''); setShowSuggestions(false); }}>✕</button>
          </div>
        </div>
      </div>

      <Pagination page={pagination.page} totalPages={pagination.totalPages || 1} total={pagination.total} onPageChange={onPage} />

      {loading ? (
        <div className="er-loading"><div className="spinner" /><p>Yükleniyor...</p></div>
      ) : matches.length === 0 ? (
        <div className="er-empty"><span className="er-empty-icon">📭</span><p>Sonuç bulunamadı.</p></div>
      ) : (
        renderCards(matches, globalMode, rowModes, setRowModes)
      )}

      <Pagination page={pagination.page} totalPages={pagination.totalPages || 1} total={pagination.total} onPageChange={onPage} />
    </div>
  );
}

export default EsporResults;
